// MUI imports
import { useState, useEffect } from "react";
import { Card, Icon, Autocomplete, TextField, Chip } from "@mui/material";

// MUI Icons imports
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Other imports
import PropTypes from "prop-types";
import {
    calculateAddonAmount,
    calculateAccommodationAmount,
} from "./hardcodedData";
import { formatAmount } from "./TravelOrderUtility";


function TripCustomizationStep({
    currency,
    baseAccommodation = [],
    accommodations,
    selectedAccommodationIds,
    accommodationTravelerMap,
    travelers,
    invalidAccommodationIds = [],
    onAccommodationToggle,
    onAccommodationTravelersChange,
    baseAddons = [],
    addons,
    selectedAddonIds,
    invalidAddonIds = [],
    addonTravelerMap,
    onAddonToggle,
    nights,
    onAddonTravelersChange,
}) {

    const [activeTab, setActiveTab] = useState(0);

    // If a validation error surfaces on a hidden tab, jump there so the
    // scroll-to-field the parent triggers has something to actually find.
    useEffect(() => {
        if (invalidAccommodationIds.length > 0) {
            setActiveTab(0);
        } else if (invalidAddonIds.length > 0) {
            setActiveTab(1);
        }
    }, [invalidAccommodationIds, invalidAddonIds]);

    const getTravelerDisplayName = (traveler) => {
        const parts = [traveler.first_name, traveler.middle_name, traveler.last_name]
            .map((p) => p?.trim())
            .filter(Boolean);
        return parts.length ? parts.join(" ") : "Unnamed traveler";
    };

    const getCapacityInfo = (linkedService) => {
        if (linkedService.min_occupancy != null || linkedService.max_occupancy != null) {
            return { label: "Occupancy", min: linkedService.min_occupancy, max: linkedService.max_occupancy };
        }
        if (linkedService.minimum_participants != null || linkedService.maximum_participants != null) {
            return { label: "Participants", min: linkedService.minimum_participants, max: linkedService.maximum_participants };
        }
        if (linkedService.min_passenger != null || linkedService.max_passenger != null) {
            return { label: "Passengers", min: linkedService.min_passenger, max: linkedService.max_passenger };
        }
        return null;
    };

    const formatCapacityRange = (capacity) => {
        if (!capacity) return null;
        const { min, max } = capacity;
        if (min != null && max != null) return `${min}-${max}`;
        if (max != null) return `Up to ${max}`;
        if (min != null) return `${min}+`;
        return null;
    };

    // `specific_day` addons render under their itinerary day's heading as an
    // upgrade option; every other scope (entire_trip, before_trip, after_trip)
    // stays in the flat "Upgrade Options" list at the end, same as before.
    const specificDayAccommodations = accommodations.filter(
        (item) => item.addon.application_scope === "specific_day" && item.addon.itinerary_day_id != null
    );
    const otherAccommodations = accommodations.filter(
        (item) => !(item.addon.application_scope === "specific_day" && item.addon.itinerary_day_id != null)
    );

    // Mirror the accommodation split: specific_day addons for non-accommodation
    // services render under their itinerary day; every other scope stays in
    // the flat "Add-ons & Experiences" list at the end.
    const specificDayAddonItems = addons.filter(
        (item) => item.addon.application_scope === "specific_day" && item.addon.itinerary_day_id != null
    );
    const flatAddonItems = addons.filter(
        (item) => !(item.addon.application_scope === "specific_day" && item.addon.itinerary_day_id != null)
    );

    // Read-only card for a base services.
    const renderIncludedService = (service) => {
        const capacity = getCapacityInfo(service);
        // const capacityLabel = formatCapacityRange(capacity);
        const capacityLabel = capacity?.max ?? null;
        return (
            <Card
                key={service.id}
                sx={{
                    borderRadius: 1.5,
                    p: 2,
                    mb: 1.25,
                    border: "1px dashed #CFE0F5",
                    bgcolor: "#FAFCFF",
                    boxShadow: "none",
                }}
            >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <MDBox flex={1}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon sx={{ fontSize: 16, color: "text.secondary" }}>
                                {service.category === "accommodation" ? "maps_home_work" : "local_activity"}
                            </Icon>
                            <MDTypography sx={{textTransform: "capitalize"}} variant="button" fontWeight="bold" color="text">
                                {service.name}
                            </MDTypography>
                        </MDBox>
                        <MDTypography sx={{textTransform: "capitalize"}} variant="caption" color="text" display="block" mt={0.25}>
                            {service.sub_category?.replace(/_/g, " ")}
                        </MDTypography>
                        <MDBox display="flex" gap={2.5} mt={1} flexWrap="wrap">
                            {service.property_name && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon sx={{ fontSize: 16, color: "text.secondary" }}>hotel</Icon>
                                    <MDTypography sx={{textTransform: "capitalize"}} variant="caption" color="text">{service.property_name}</MDTypography>
                                </MDBox>
                            )}
                            {(service.property_address || service.activity_location || service.venue) && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon sx={{ fontSize: 16, color: "text.secondary" }}>place</Icon>
                                    <MDTypography variant="caption" color="text">
                                        {service.property_address || service.activity_location || service.venue}
                                    </MDTypography>
                                </MDBox>
                            )}
                            {(service.check_in_time || service.check_out_time) && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon sx={{ fontSize: 16, color: "text.secondary" }}>schedule</Icon>
                                    <MDTypography variant="caption" color="text">
                                        {service.check_in_time?.slice(0, 5)}–{service.check_out_time?.slice(0, 5)}
                                    </MDTypography>
                                </MDBox>
                            )}
                            {(service.start_time || service.end_time) && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon sx={{ fontSize: 16, color: "text.secondary" }}>schedule</Icon>
                                    <MDTypography variant="caption" color="text">
                                        {service.start_time?.slice(0, 5)}–{service.end_time?.slice(0, 5)}
                                    </MDTypography>
                                </MDBox>
                            )}
                            {capacityLabel && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                    <MDTypography variant="caption" color="text">{capacityLabel} guests</MDTypography>
                                </MDBox>
                            )}
                        </MDBox>
                    </MDBox>
                    <Chip
                        label="Included"
                        size="small"
                        sx={{
                            flexShrink: 0,
                            bgcolor: "#E6F4EA",
                            color: "#1E6C2A",
                            fontWeight: 600,
                            border: "1px solid #B7E0C0",
                        }}
                    />
                </MDBox>
            </Card>
        );
    };

    // Selectable accommodation upgrade card — used both under a day heading
    // (for specific_day addons) and in the flat Upgrade Options list.
    const renderAccommodationOption = (item) => {
        const { addon, linkedService } = item;
        const selected = selectedAccommodationIds.includes(addon.id);
        const capacity = getCapacityInfo(linkedService);
        // const capacityLabel = formatCapacityRange(capacity);
        const capacityLabel = capacity?.max ?? null;
        const maxOccupancy = capacity?.max ?? null;
        const assignedTravelerIds = accommodationTravelerMap[addon.id] || [];
        const assignedTravelers = travelers.filter((t) => assignedTravelerIds.includes(t.id));
        const capReached = maxOccupancy != null && assignedTravelerIds.length >= maxOccupancy;
        const isInvalid = invalidAccommodationIds.includes(linkedService.id);
        const effectiveCount = assignedTravelerIds.length || 0;
        const accommodationTotal = addon.addon_price * nights * effectiveCount;

        return (
            <Card
                key={linkedService.id}
                onClick={() => onAccommodationToggle(addon.id)}
                sx={{
                    borderRadius: 1.5,
                    p: 2,
                    cursor: "pointer",
                    border: selected ? "2px solid #1565C8" : "1px solid #E8EDF5",
                    bgcolor: selected ? "#F7FAFF" : "#fff",
                    boxShadow: "none",
                    transition: "all 0.2s ease",
                }}
            >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <MDBox flex={1}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                                <Icon sx={{ fontSize: 16, color: "text.secondary" }}>maps_home_work</Icon>
                                <MDTypography variant="button" fontWeight="bold" color="text">
                                    {linkedService.name}
                                </MDTypography>
                            </MDBox>
                            {selected && <CheckCircleIcon color="info" sx={{ fontSize: 18 }} />}
                        </MDBox>

                        <MDTypography variant="caption" color="text" display="block" mt={0.25}>
                            {linkedService.sub_category?.replace(/_/g, " ")}
                        </MDTypography>

                        <MDBox display="flex" gap={2.5} mt={1}>
                            {linkedService.property_name && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon sx={{ fontSize: 16, color: "text.secondary" }}>hotel</Icon>
                                    <MDTypography variant="caption" color="text">
                                        {linkedService.property_name}
                                    </MDTypography>
                                </MDBox>
                            )}
                            {(linkedService.check_in_time || linkedService.check_out_time) && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon sx={{ fontSize: 16, color: "text.secondary" }}>schedule</Icon>
                                    <MDTypography variant="caption" color="text">
                                        {linkedService.check_in_time?.slice(0, 5)}–{linkedService.check_out_time?.slice(0, 5)}
                                    </MDTypography>
                                </MDBox>
                            )}
                            {capacityLabel && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                    <MDTypography variant="caption" color="text">
                                        {capacityLabel} guests
                                    </MDTypography>
                                </MDBox>
                            )}
                        </MDBox>

                        {(linkedService.property_address) && (
                            <MDBox display="flex" alignItems="center" pt={1.25} gap={0.5}>
                                <Icon sx={{ fontSize: 16, color: "text.secondary" }}>place</Icon>
                                <MDTypography variant="caption" color="text">
                                    {linkedService.property_address}
                                </MDTypography>
                            </MDBox>
                        )}
                    </MDBox>

                    <MDBox textAlign="right" sx={{ flexShrink: 0 }}>
                        <MDBox display="flex" alignItems="center" justifyContent="flex-end">
                            <MDTypography variant="h6" fontWeight="bold" color={selected ? "info" : "dark"}>
                                {formatAmount(addon.addon_price * nights, currency)}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                                / person
                            </MDTypography>
                        </MDBox>
                        {effectiveCount > 0 && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                Selected total: {formatAmount(accommodationTotal, currency)}
                            </MDTypography>
                        )}
                    </MDBox>
                </MDBox>

                {selected && (
                    <MDBox
                        id={`accommodation-travelers-${addon.id}`}
                        mt={1.5}
                        pt={1.25}
                        sx={{ borderTop: "1px dashed #E0E6EF" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                            Who's staying here?
                        </MDTypography>
                        <Autocomplete
                            multiple
                            size="small"
                            options={travelers}
                            value={assignedTravelers}
                            getOptionLabel={getTravelerDisplayName}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            getOptionDisabled={(t) =>
                                capReached && !assignedTravelerIds.includes(t.id)
                            }
                            onChange={(_, newValue) => {
                                const capped = maxOccupancy != null ? newValue.slice(0, maxOccupancy) : newValue;
                                onAccommodationTravelersChange(addon.id, capped.map((t) => t.id));
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((t, i) => (
                                    <Chip {...getTagProps({ index: i })} key={t.id} size="small" label={getTravelerDisplayName(t)} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} placeholder={assignedTravelers.length === 0 ? "Select travelers" : ""} />
                            )}
                        />
                        {maxOccupancy != null && (
                            <MDTypography variant="caption" color={capReached ? "error" : "text"} display="block" mt={0.5}>
                                {assignedTravelerIds.length} / {maxOccupancy} selected
                                {capReached ? " : occupancy limit reached" : ""}
                            </MDTypography>
                        )}
                        {isInvalid && (
                            <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                                Please select at least one traveler for this accommodation.
                            </MDTypography>
                        )}
                    </MDBox>
                )}
            </Card>
        );
    };

    // Selectable add-on/experience card — used both under a day heading (for
    // specific_day addons) and in the flat Add-ons & Experiences list.
    const renderAddonOption = (item) => {
        const { addon, linkedService } = item;
        const selected = selectedAddonIds.includes(addon.id);
        const isInvalid = invalidAddonIds.includes(addon.id);
        const assignedTravelerIds = addonTravelerMap[addon.id] || [];
        const assignedTravelers = travelers.filter((t) => assignedTravelerIds.includes(t.id));
        const minQuantity = addon.min_quantity ?? null;
        const maxQuantity = addon.max_quantity ?? null;
        const capReached = maxQuantity != null && assignedTravelerIds.length >= maxQuantity;

        const effectiveCount = assignedTravelerIds.length || 0;
        const amount = calculateAddonAmount(addon, effectiveCount);

        // linkedService is null for standalone addons (e.g. Bungy Jumping) -
        // guard the capacity lookup rather than crashing on a missing service.
        const capacity = linkedService ? getCapacityInfo(linkedService) : null;
        const capacityLabel = formatCapacityRange(capacity);
        const availableCapacity = capacity?.max ?? null;

        return (
            <Card
                key={addon.id}
                onClick={() => onAddonToggle(addon.id)}
                sx={{
                    borderRadius: 1.5,
                    p: 2,
                    cursor: "pointer",
                    border: selected ? "2px solid #1565C8" : "1px solid #E8EDF5",
                    bgcolor: selected ? "#F7FAFF" : "#fff",
                    boxShadow: "none",
                    transition: "all 0.2s ease",
                }}
            >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <MDBox flex={1}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                                <Icon sx={{ fontSize: 16, color: "text.secondary" }}>local_activity</Icon>
                                <MDTypography variant="button" fontWeight="bold" color="text">
                                    {addon.name}
                                </MDTypography>
                            </MDBox>
                            {selected && <CheckCircleIcon color="info" sx={{ fontSize: 18 }} />}
                        </MDBox>

                        <MDTypography variant="caption" color="text" display="block" mt={0.25}>
                            {(addon.type || addon.addon_type || "").replace(/_/g, " ")}
                            {(addon.type === "arrive_early" || addon.type === "stay_behind"
                                || addon.addon_type === "arrive_early" || addon.addon_type === "stay_behind")
                                && (addon.number_of_days || addon.extension_days_count)
                                ? ` · ${addon.number_of_days || addon.extension_days_count} day${
                                    Number(addon.number_of_days || addon.extension_days_count) === 1 ? "" : "s"
                                }`
                                : ""}
                        </MDTypography>

                        <MDBox display="flex" gap={2.5} mt={1} flexWrap="wrap">
                            {(linkedService?.activity_location || linkedService?.venue) && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon sx={{ fontSize: 16, color: "text.secondary" }}>place</Icon>
                                    <MDTypography variant="caption" color="text">
                                        {linkedService.activity_location || linkedService.venue}
                                    </MDTypography>
                                </MDBox>
                            )}
                            {(linkedService?.start_time || linkedService?.end_time) && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon sx={{ fontSize: 16, color: "text.secondary" }}>schedule</Icon>
                                    <MDTypography variant="caption" color="text">
                                        {linkedService.start_time?.slice(0, 5)}–{linkedService.end_time?.slice(0, 5)}
                                    </MDTypography>
                                </MDBox>
                            )}
                            {availableCapacity != null && (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                    <MDTypography variant="caption" color="text">
                                        {availableCapacity} seats left
                                    </MDTypography>
                                </MDBox>
                            )}
                        </MDBox>

                        {/* Standalone addons (no linked service) carry their own copy */}
                        {!linkedService && addon.website_description && (
                            <MDTypography
                                variant="caption"
                                color="text"
                                display="block"
                                mt={1}
                                dangerouslySetInnerHTML={{ __html: addon.website_description }}
                            />
                        )}
                    </MDBox>

                    <MDBox textAlign="right" sx={{ flexShrink: 0 }}>
                        <MDTypography variant="h6" fontWeight="bold" color={selected ? "info" : "dark"}>
                            {formatAmount(addon.addon_price, currency)}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                            {(addon.pricing_basis || "per_passenger") === "fixed" ? "fixed" : "/ person"}
                        </MDTypography>
                        {effectiveCount > 0 && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                Selected total: {formatAmount(amount, currency)}
                            </MDTypography>
                        )}
                    </MDBox>
                </MDBox>

                {selected && (
                    <MDBox
                        id={`addon-travelers-${addon.id}`}
                        mt={1.5}
                        pt={1.25}
                        sx={{ borderTop: "1px dashed #E0E6EF" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                            Who's joining?
                        </MDTypography>
                        <Autocomplete
                            multiple
                            size="small"
                            options={travelers}
                            value={assignedTravelers}
                            getOptionLabel={getTravelerDisplayName}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            getOptionDisabled={(t) => capReached && !assignedTravelerIds.includes(t.id)}
                            onChange={(_, newValue) => {
                                const capped = maxQuantity != null ? newValue.slice(0, maxQuantity) : newValue;
                                onAddonTravelersChange(addon.id, capped.map((t) => t.id));
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((t, i) => (
                                    <Chip {...getTagProps({ index: i })} key={t.id} size="small" label={getTravelerDisplayName(t)} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} placeholder={assignedTravelers.length === 0 ? "Select travelers" : ""} />
                            )}
                        />
                        {(maxQuantity != null || minQuantity != null) && (
                            <MDTypography variant="caption" color={capReached ? "error" : "text"} display="block" mt={0.5}>
                                {assignedTravelerIds.length} traveler{assignedTravelerIds.length === 1 ? "" : "s"} selected
                                {capReached ? " : limit reached" : ""}
                            </MDTypography>
                        )}
                        {isInvalid && (
                            <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                                    Please select at least one traveler for this add-on.
                            </MDTypography>
                        )}
                    </MDBox>
                )}
            </Card>
        );
    };

    return (
        <MDBox>

            {/* Header */}

            <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
                Customize Your Trip
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={2}>
                Here's what's already included and what you can upgrade or add on.
            </MDTypography>

            {/* Tab Header */}

            <MDBox
                sx={{
                    display: "flex",
                    gap: 4,
                    borderBottom: "1px solid #E8EDF5",
                    mb: 3,
                }}
            >
                {[
                    { label: "Accommodation", icon: "hotel", hasError: invalidAccommodationIds.length > 0 },
                    { label: "Add-ons & Experiences", icon: "local_activity", hasError: invalidAddonIds.length > 0 },
                ].map((tab, index) => {
                    const isActive = activeTab === index;
                    return (
                        <MDBox
                            key={tab.label}
                            onClick={() => setActiveTab(index)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                pb: 1.5,
                                cursor: "pointer",
                                userSelect: "none",
                                borderBottom: isActive ? "2px solid #1565C8" : "2px solid transparent",
                                mb: "-1px",
                                transition: "all 0.2s ease",
                            }}
                        >
                            <Icon sx={{ fontSize: 18, color: isActive ? "#1565C8" : "text.secondary" }}>
                                {tab.icon}
                            </Icon>
                            <MDTypography
                                variant="button"
                                fontWeight={isActive ? "bold" : "medium"}
                                sx={{
                                    color: isActive ? "#1565C8" : "text.secondary",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                {tab.label}
                                {tab.hasError && (
                                    <MDBox
                                        component="span"
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: "50%",
                                            bgcolor: "error.main",
                                            display: "inline-block",
                                        }}
                                    />
                                )}
                            </MDTypography>
                        </MDBox>
                    );
                })}
            </MDBox>

            {/* ----------------- ACCOMMODATION TAB ----------------- */}

            {activeTab === 0 && (
                <MDBox>
                    {baseAccommodation.length > 0 && (
                        <MDBox mb={3}>
                            {baseAccommodation.length > 0 && (
                                <MDBox mb={3}>
                                    <MDTypography variant="h5" fontWeight="bold" display="block" mb={1}>
                                        Included in Your Package
                                    </MDTypography>
                                    {baseAccommodation.map((day) => {
                                        const dayUpgrades = specificDayAccommodations.filter(
                                            (item) => item.addon.itinerary_day_id === day.day_id
                                        );
                                        return (
                                            <MDBox key={day.day_number} mb={2}>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    color="info"
                                                    display="block"
                                                    mb={1}
                                                    sx={{ textTransform: "uppercase", letterSpacing: "0.03em" }}
                                                >
                                                    Day {day.day_number}
                                                    {day.day_title ? ` · ${day.day_title}` : ""}
                                                </MDTypography>

                                                {day.accommodations.map(renderIncludedService)}

                                                {dayUpgrades.length > 0 && (
                                                    <MDBox mt={day.accommodations.length > 0 ? 1 : 0}>
                                                        <MDTypography variant="caption" color="text" display="block" mb={0.75}>
                                                            {day.accommodations.length > 0
                                                                ? "Upgrade available for this day"
                                                                : "Optional upgrade available for this day"}
                                                        </MDTypography>
                                                        <MDBox display="flex" flexDirection="column" gap={1.5}>
                                                            {dayUpgrades.map(renderAccommodationOption)}
                                                        </MDBox>
                                                    </MDBox>
                                                )}
                                            </MDBox>
                                        );
                                    })}
                                </MDBox>
                            )}
                        </MDBox>
                    )}

                    {/* Entire Trip Header */}

                    <MDTypography
                        variant="h5"
                        fontWeight="bold"
                        color="black"
                        display="block"
                        mb={1}
                        sx={{ textTransform: "uppercase", letterSpacing: "0.03em" }}
                    >
                        Entire Trip
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                        {
                            otherAccommodations.length > 0
                                ? "Prefer something else? Choose a room upgrade below."
                                : "No upgrades available"
                        }
                    </MDTypography>

                    {/* Upgrade Accommodation*/}

                    <MDBox display="flex" flexDirection="column" gap={1.5}>
                        {otherAccommodations.map((item) => renderAccommodationOption(item))}
                    </MDBox>

                    {selectedAccommodationIds.length > 0 && (
                        <MDBox mt={2} p={1.5} borderRadius={2} sx={{ bgcolor: "#F7FAFF", border: "1px solid #E3F2FD" }}>
                            {selectedAccommodationIds.map((accId) => {
                                const accItem = accommodations.find((item) => item.addon.id === accId);
                                if (!accItem) return null;
                                const travelerCount = (accommodationTravelerMap[accId] || []).length;
                                const amount = calculateAccommodationAmount(accItem, nights, travelerCount);

                                return (
                                    <MDBox
                                        key={accId}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={selectedAccommodationIds.length > 1 ? 0.75 : 0}
                                    >
                                        <MDTypography variant="caption" color="text">
                                            {accItem.linkedService.name} · {nights} night{nights > 1 ? "s" : ""}
                                            {travelerCount > 0 ? ` · ${travelerCount} traveler${travelerCount > 1 ? "s" : ""}` : ""}
                                        </MDTypography>
                                        <MDTypography variant="button" fontWeight="bold" color="dark">
                                            {formatAmount(amount, currency)}
                                        </MDTypography>
                                    </MDBox>
                                );
                            })}

                            {selectedAccommodationIds.length > 1 && (
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={1} pt={1} sx={{ borderTop: "1px solid #E3F2FD" }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text">
                                        Total accommodation charge
                                    </MDTypography>
                                    <MDTypography variant="button" fontWeight="bold" color="dark">
                                        {formatAmount(
                                            selectedAccommodationIds.reduce((sum, accId) => {
                                                const accItem = accommodations.find((item) => item.addon.id === accId);
                                                const travelerCount = (accommodationTravelerMap[accId] || []).length;
                                                return sum + (accItem ? calculateAccommodationAmount(accItem, nights, travelerCount) : 0);
                                            }, 0), currency
                                        )}
                                    </MDTypography>
                                </MDBox>
                            )}
                        </MDBox>
                    )}
                </MDBox>
            )}

            {/* ----------------- ADD-ONS & EXPERIENCES TAB ----------------- */}

            {activeTab === 1 && (
                <MDBox>
                    {baseAddons.length > 0 && (
                        <MDBox mb={3}>
                            <MDTypography variant="h5" fontWeight="bold" display="block" mb={1}>
                                Get Add-ons
                            </MDTypography>
                            {baseAddons.map((day) => {
                                const dayUpgrades = specificDayAddonItems.filter(
                                    (item) => item.addon.itinerary_day_id === day.day_id
                                );
                                return (
                                    <MDBox key={day.day_number} mb={2}>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            color="info"
                                            display="block"
                                            mb={1}
                                            sx={{ textTransform: "uppercase", letterSpacing: "0.03em" }}
                                        >
                                            Day {day.day_number}
                                            {day.day_title ? ` · ${day.day_title}` : ""}
                                        </MDTypography>

                                        {day.services.map(renderIncludedService)}

                                        {dayUpgrades.length > 0 && (
                                            <MDBox mt={day.services.length > 0 ? 1 : 0}>
                                                <MDTypography variant="caption" color="text" display="block" mb={0.75}>
                                                    {day.services.length > 0
                                                        ? "Optional add-on for this day"
                                                        : "Optional add-on available for this day"}
                                                </MDTypography>
                                                <MDBox display="flex" flexDirection="column" gap={1.5}>
                                                    {dayUpgrades.map(renderAddonOption)}
                                                </MDBox>
                                            </MDBox>
                                        )}
                                    </MDBox>
                                );
                            })}
                        </MDBox>
                    )}

                    <MDTypography
                        variant="h5"
                        fontWeight="bold"
                        color="black"
                        display="block"
                        mb={1}
                        sx={{ textTransform: "uppercase", letterSpacing: "0.03em" }}
                    >
                        Entire Trip
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                        {flatAddonItems.length > 0
                            ? "Want something extra? Add optional experiences below."
                            : "No additional add-ons available"}
                    </MDTypography>

                    <MDBox display="flex" flexDirection="column" gap={1.5}>
                        {flatAddonItems.map((item) => renderAddonOption(item))}
                    </MDBox>
                </MDBox>
            )}
        </MDBox>
    );
}

TripCustomizationStep.propTypes = {
    baseAccommodation: PropTypes.arrayOf(
        PropTypes.shape({
            day_number: PropTypes.number,
            day_title: PropTypes.string,
            accommodations: PropTypes.arrayOf(PropTypes.object),
        })
    ),
    baseAddons: PropTypes.arrayOf(
        PropTypes.shape({
            day_id: PropTypes.number,
            day_number: PropTypes.number,
            day_title: PropTypes.string,
            services: PropTypes.arrayOf(PropTypes.object),
        })
    ),
    accommodations: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedAccommodationIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    onAccommodationToggle: PropTypes.func.isRequired,
    accommodationTravelerMap: PropTypes.object.isRequired,
    onAccommodationTravelersChange: PropTypes.func.isRequired,
    invalidAccommodationIds: PropTypes.arrayOf(PropTypes.string),
    addons: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedAddonIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    onAddonToggle: PropTypes.func.isRequired,
    invalidAddonIds: PropTypes.arrayOf(PropTypes.string),
    nights: PropTypes.number.isRequired,
    travelers: PropTypes.arrayOf(PropTypes.object).isRequired,
    addonTravelerMap: PropTypes.object.isRequired,
    onAddonTravelersChange: PropTypes.func.isRequired,
};

export default TripCustomizationStep;